import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Clock, User, Scissors, MapPin, Truck, ChevronLeft, ChevronRight,
  Plus, Search, Filter, ArrowLeft, MoreVertical, Edit2, Trash2, X, Check,
  Save, Loader2, DollarSign, CalendarDays, List, Settings, Briefcase, Map,
  LayoutGrid, Users, Mail, Phone, Home, Navigation, AlertCircle
} from 'lucide-react';
import { toolsService } from '../../../services/toolsService';

// ============================================================
// CONSTANTS & CONFIG
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  CALENDAR: 'CALENDAR',
  APPOINTMENTS_LIST: 'APPOINTMENTS_LIST',
  SERVICES: 'SERVICES',
  STAFF: 'STAFF',
  LOCATIONS: 'LOCATIONS',
  BOOKING_FORM: 'BOOKING_FORM',
  SERVICE_FORM: 'SERVICE_FORM',
  STAFF_FORM: 'STAFF_FORM',
  LOCATION_FORM: 'LOCATION_FORM'
};

const AppointmentStatus = {
  PENDING: { id: 'PENDING', label: 'Pending', color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  CONFIRMED: { id: 'CONFIRMED', label: 'Confirmed', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  COMPLETED: { id: 'COMPLETED', label: 'Completed', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  CANCELLED: { id: 'CANCELLED', label: 'Cancelled', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  NOSHOW: { id: 'NOSHOW', label: 'No Show', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-700' }
};

const CalendarMode = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH'
};

const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8 AM to 8 PM
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const formatCurrency = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(amount || 0);
const formatDate = (date) => new Date(date).toLocaleDateString('en-ZA', { weekday: 'short', month: 'short', day: 'numeric' });
const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

// ============================================================
// UI COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, color = 'indigo', onClick }) => (
  <button onClick={onClick} disabled={!onClick} className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

// ============================================================
// FORMS
// ============================================================

const BookingForm = ({ booking, services, staff, locations, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    customerName: '', customerEmail: '', customerPhone: '',
    serviceId: '', staffId: '', locationId: '', type: 'ONSITE',
    date: new Date().toISOString().split('T')[0], 
    time: '09:00',
    duration: 60,
    address: '', notes: '', status: 'PENDING', travelFee: 0, price: 0,
    ...booking
  });

  // Auto-update price and duration when service changes
  useEffect(() => {
    if (formData.serviceId) {
      const svc = services.find(s => s.id === formData.serviceId);
      if (svc) {
        setFormData(prev => ({
          ...prev,
          price: svc.basePrice || 0,
          duration: svc.duration || 60,
          type: svc.isHouseCallAvailable ? prev.type : 'ONSITE' // Reset to onsite if housecall not allowed
        }));
      }
    }
  }, [formData.serviceId, services]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const startDateTime = new Date(`${formData.date}T${formData.time}`);
    const endDateTime = new Date(startDateTime.getTime() + (formData.duration * 60000));
    
    onSave({
      ...formData,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
    });
  };

  const selectedService = services.find(s => s.id === formData.serviceId);

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{booking?.id ? 'Edit Appointment' : 'New Appointment'}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Customer Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <h3 className="font-semibold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2"><User className="w-4 h-4"/> Customer Details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input required type="text" value={formData.customerName} onChange={e => setFormData({...formData, customerName: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" placeholder="Jane Doe" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={formData.customerPhone} onChange={e => setFormData({...formData, customerPhone: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" placeholder="082..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={formData.customerEmail} onChange={e => setFormData({...formData, customerEmail: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" placeholder="jane@email.com" />
              </div>
            </div>
          </div>

          {/* Service & Type Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <h3 className="font-semibold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2"><Briefcase className="w-4 h-4"/> Service Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service *</label>
              <select required value={formData.serviceId} onChange={e => setFormData({...formData, serviceId: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl">
                <option value="">Select Service...</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration} min - {formatCurrency(s.basePrice)})</option>)}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Appointment Type</label>
              <div className="flex gap-4 p-1 bg-gray-50 rounded-xl">
                <button type="button" onClick={() => setFormData({...formData, type: 'ONSITE'})} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${formData.type === 'ONSITE' ? 'bg-white shadow text-indigo-600 ring-1 ring-black/5' : 'text-gray-500 hover:bg-gray-100'}`}>
                  <MapPin className="w-4 h-4" /> On-Site
                </button>
                <button 
                  type="button" 
                  disabled={selectedService && !selectedService.isHouseCallAvailable}
                  onClick={() => setFormData({...formData, type: 'HOUSECALL'})} 
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${formData.type === 'HOUSECALL' ? 'bg-white shadow text-indigo-600 ring-1 ring-black/5' : 'text-gray-500 hover:bg-gray-100'} ${selectedService && !selectedService.isHouseCallAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <Truck className="w-4 h-4" /> House Call
                </button>
              </div>
            </div>

            {formData.type === 'HOUSECALL' ? (
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 space-y-3 animate-in fade-in slide-in-from-top-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Address *</label>
                  <textarea required rows={2} value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl bg-white" placeholder="Street, Suburb, City..." />
                </div>
                <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Travel Fee (ZAR)</label>
                   <input type="number" min="0" value={formData.travelFee} onChange={e => setFormData({...formData, travelFee: parseFloat(e.target.value)})} className="w-full px-4 py-2 border border-gray-200 rounded-xl bg-white" />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Station / Room / Chair</label>
                <select value={formData.locationId} onChange={e => setFormData({...formData, locationId: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl">
                  <option value="">No Specific Station</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Scheduling Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <h3 className="font-semibold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2"><Clock className="w-4 h-4"/> Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input required type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                <input required type="time" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                 <input type="number" value={formData.duration} onChange={e => setFormData({...formData, duration: parseInt(e.target.value)})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <select value={formData.staffId} onChange={e => setFormData({...formData, staffId: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl">
                  <option value="">Any Available</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
               <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl">
                 {Object.values(AppointmentStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
               </select>
            </div>

            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
               <textarea rows={2} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-4 py-2 border border-gray-200 rounded-xl" placeholder="Internal notes..." />
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="bg-gray-900 rounded-2xl p-6 text-white shadow-xl">
             <h3 className="font-semibold text-gray-100 border-b border-gray-700 pb-2 mb-4">Payment Summary</h3>
             <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Service Price</span>
                <input 
                  type="number" 
                  className="bg-transparent text-right w-32 border-b border-gray-700 focus:border-indigo-500 outline-none text-white"
                  value={formData.price}
                  onChange={e => setFormData({...formData, price: parseFloat(e.target.value)})}
                />
             </div>
             {formData.type === 'HOUSECALL' && (
               <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-400">Travel Fee</span>
                  <span>{formatCurrency(formData.travelFee || 0)}</span>
               </div>
             )}
             <div className="border-t border-gray-700 pt-4 flex justify-between items-center text-xl font-bold">
                <span>Total</span>
                <span>{formatCurrency((formData.price || 0) + (formData.travelFee || 0))}</span>
             </div>
          </div>

          <button type="submit" disabled={saving} className="w-full py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center justify-center gap-2 font-bold shadow-lg shadow-indigo-200 transition-all transform active:scale-95">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} 
            {booking?.id ? 'Update Appointment' : 'Confirm Booking'}
          </button>
        </div>
      </div>
    </form>
  );
};

const ServiceForm = ({ service, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', duration: 60, basePrice: 0,
    isHouseCallAvailable: false, color: 'blue', ...service
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="max-w-xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3"><button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600"/></button><h2 className="text-2xl font-bold">{service?.id ? 'Edit Service' : 'New Service'}</h2></div>
      <div className="bg-white p-6 rounded-2xl border border-gray-100 space-y-4 shadow-sm">
         <input required placeholder="Service Name" className="w-full px-4 py-2 border rounded-xl" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
         <textarea placeholder="Description" className="w-full px-4 py-2 border rounded-xl" rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
         <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 ml-1">Duration (min)</label>
              <input type="number" placeholder="60" className="w-full px-4 py-2 border rounded-xl" value={formData.duration} onChange={e => setFormData({...formData, duration: parseInt(e.target.value)})} />
            </div>
            <div>
              <label className="text-xs text-gray-500 ml-1">Base Price (R)</label>
              <input type="number" placeholder="0.00" className="w-full px-4 py-2 border rounded-xl" value={formData.basePrice} onChange={e => setFormData({...formData, basePrice: parseFloat(e.target.value)})} />
            </div>
         </div>
         <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <input type="checkbox" id="hc" className="w-5 h-5 text-indigo-600 rounded" checked={formData.isHouseCallAvailable} onChange={e => setFormData({...formData, isHouseCallAvailable: e.target.checked})} />
            <label htmlFor="hc" className="text-sm font-medium text-gray-700 flex items-center gap-2"><Truck className="w-4 h-4 text-gray-400"/> Available for House Calls</label>
         </div>
         <button type="submit" disabled={saving} className="w-full py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700">Save Service</button>
      </div>
    </form>
  );
};

const StaffForm = ({ staff, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', role: 'Provider', ...staff
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="max-w-xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3"><button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600"/></button><h2 className="text-2xl font-bold">{staff?.id ? 'Edit Staff' : 'New Staff'}</h2></div>
      <div className="bg-white p-6 rounded-2xl border border-gray-100 space-y-4 shadow-sm">
         <input required placeholder="Full Name" className="w-full px-4 py-2 border rounded-xl" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
         <input type="email" placeholder="Email" className="w-full px-4 py-2 border rounded-xl" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
         <input type="tel" placeholder="Phone" className="w-full px-4 py-2 border rounded-xl" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
         <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full px-4 py-2 border rounded-xl">
              <option value="Provider">Service Provider</option>
              <option value="Admin">Reception / Admin</option>
            </select>
         </div>
         <button type="submit" disabled={saving} className="w-full py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700">Save Staff Member</button>
      </div>
    </form>
  );
};

const LocationForm = ({ location, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', color: 'blue', ...location
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="max-w-xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3"><button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600"/></button><h2 className="text-2xl font-bold">{location?.id ? 'Edit Location' : 'New Location'}</h2></div>
      <div className="bg-white p-6 rounded-2xl border border-gray-100 space-y-4 shadow-sm">
         <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Name (e.g., Room 1, Chair A)</label>
            <input required placeholder="Location Name" className="w-full px-4 py-2 border rounded-xl" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
         </div>
         <button type="submit" disabled={saving} className="w-full py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700">Save Location</button>
      </div>
    </form>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const AppointmentTool = ({ bookId, onBack }) => {
  // State
  const [view, setView] = useState(ViewState.DASHBOARD);
  const [calendarMode, setCalendarMode] = useState(CalendarMode.DAY);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Data
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Loading/Saving States
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Selection for Editing
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Filters (List View)
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => { loadData(); }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [app, srv, stf, loc] = await Promise.all([
        toolsService.getToolData(bookId, 'appointments'),
        toolsService.getToolData(bookId, 'appointment-services'),
        toolsService.getToolData(bookId, 'appointment-staff'),
        toolsService.getToolData(bookId, 'appointment-locations')
      ]);
      setAppointments(app || []);
      setServices(srv || []);
      setStaff(stf || []);
      setLocations(loc || []);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSave = async (collection, data, targetView) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, collection, data);
      await loadData();
      setView(targetView);
    } catch (err) { 
      alert('Failed to save data. Please try again.');
    } finally { 
      setSaving(false); 
    }
  };

  const handleDelete = async (collection, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await toolsService.deleteToolData(bookId, collection, id);
      await loadData();
    } catch (err) { console.error(err); }
  };

  // --- RENDERERS ---

  const renderDashboard = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayApps = appointments.filter(a => a.startTime.startsWith(today) && a.status !== 'CANCELLED');
    const revenue = appointments.filter(a => a.status === 'COMPLETED').reduce((acc, a) => acc + (a.price || 0) + (a.travelFee || 0), 0);
    const pending = appointments.filter(a => a.status === 'PENDING').length;

    return (
      <div className="space-y-6 animate-in fade-in">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={CalendarDays} label="Today's Bookings" value={todayApps.length} color="blue" onClick={() => setView(ViewState.CALENDAR)} />
          <StatCard icon={AlertCircle} label="Pending Approval" value={pending} color="orange" onClick={() => { setStatusFilter('PENDING'); setView(ViewState.APPOINTMENTS_LIST); }} />
          <StatCard icon={Users} label="Staff Members" value={staff.length} color="purple" onClick={() => setView(ViewState.STAFF)} />
          <StatCard icon={DollarSign} label="Total Revenue" value={formatCurrency(revenue)} color="green" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 flex flex-col h-full">
              <div className="flex justify-between items-center mb-6">
                 <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-600"/> Upcoming Schedule</h3>
                 <button onClick={() => setView(ViewState.APPOINTMENTS_LIST)} className="text-sm text-indigo-600 font-medium hover:underline">View All</button>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto max-h-[400px]">
                 {appointments
                    .filter(a => new Date(a.startTime) >= new Date() && a.status !== 'CANCELLED')
                    .sort((a,b) => new Date(a.startTime) - new Date(b.startTime))
                    .slice(0, 10)
                    .map(app => (
                    <div key={app.id} className="flex justify-between items-center p-4 bg-gray-50 hover:bg-white hover:shadow-md transition-all rounded-xl border border-gray-100 group">
                       <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center text-sm font-bold ${app.type === 'HOUSECALL' ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'}`}>
                             <span>{new Date(app.startTime).getDate()}</span>
                             <span className="text-[10px] uppercase font-normal">{new Date(app.startTime).toLocaleDateString('en-US', {month:'short'})}</span>
                          </div>
                          <div>
                             <p className="font-bold text-gray-900">{app.customerName}</p>
                             <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                <span>{formatTime(app.startTime)}</span>
                                <span>•</span>
                                <span>{services.find(s => s.id === app.serviceId)?.name}</span>
                                {app.type === 'HOUSECALL' && <span className="flex items-center gap-1 text-orange-600 bg-orange-50 px-1 rounded"><Truck className="w-3 h-3"/> Mobile</span>}
                             </div>
                          </div>
                       </div>
                       <div className="flex items-center gap-3">
                         <span className={`text-xs px-2 py-1 rounded-full font-medium ${AppointmentStatus[app.status]?.bgColor} ${AppointmentStatus[app.status]?.textColor}`}>
                            {AppointmentStatus[app.status]?.label}
                         </span>
                         <button onClick={() => { setSelectedBooking(app); setView(ViewState.BOOKING_FORM); }} className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-indigo-600"><Edit2 className="w-4 h-4"/></button>
                       </div>
                    </div>
                 ))}
                 {appointments.length === 0 && (
                   <div className="text-center py-10 text-gray-400">
                     <Calendar className="w-10 h-10 mx-auto mb-2 opacity-20"/>
                     <p>No upcoming appointments found.</p>
                   </div>
                 )}
              </div>
           </div>
           
           <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2"><Settings className="w-5 h-5 text-indigo-600"/> Quick Actions</h3>
              <div className="grid grid-cols-1 gap-3">
                 <button onClick={() => { setSelectedBooking(null); setView(ViewState.BOOKING_FORM); }} className="p-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center justify-center gap-3 transition-colors font-semibold shadow-lg shadow-indigo-200">
                   <Plus className="w-5 h-5"/> New Appointment
                 </button>
                 <div className="h-px bg-gray-100 my-2"></div>
                 <button onClick={() => { setSelectedService(null); setView(ViewState.SERVICE_FORM); }} className="p-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 flex items-center gap-3 transition-colors text-sm font-medium">
                   <Scissors className="w-5 h-5 text-gray-400"/> Manage Services
                 </button>
                 <button onClick={() => { setSelectedStaff(null); setView(ViewState.STAFF_FORM); }} className="p-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 flex items-center gap-3 transition-colors text-sm font-medium">
                   <Users className="w-5 h-5 text-gray-400"/> Manage Staff
                 </button>
                 <button onClick={() => { setSelectedLocation(null); setView(ViewState.LOCATION_FORM); }} className="p-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 flex items-center gap-3 transition-colors text-sm font-medium">
                   <MapPin className="w-5 h-5 text-gray-400"/> Manage Locations
                 </button>
              </div>
           </div>
        </div>
      </div>
    );
  };

  const renderCalendar = () => {
    const handleDateChange = (days) => {
       const d = new Date(selectedDate);
       d.setDate(d.getDate() + days);
       setSelectedDate(d);
    };

    return (
     <div className="space-y-4 h-[calc(100vh-140px)] flex flex-col">
        {/* Calendar Header */}
        <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-100 shadow-sm shrink-0">
           <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
                <button onClick={() => handleDateChange(calendarMode === CalendarMode.WEEK ? -7 : -1)} className="p-1 hover:bg-white rounded shadow-sm transition-all"><ChevronLeft className="w-5 h-5 text-gray-600"/></button>
                <button onClick={() => setSelectedDate(new Date())} className="text-xs font-bold px-2 text-gray-600">Today</button>
                <button onClick={() => handleDateChange(calendarMode === CalendarMode.WEEK ? 7 : 1)} className="p-1 hover:bg-white rounded shadow-sm transition-all"><ChevronRight className="w-5 h-5 text-gray-600"/></button>
              </div>
              <h2 className="text-lg font-bold text-gray-900 w-48">{formatDate(selectedDate)}</h2>
           </div>
           <div className="flex gap-2">
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button onClick={() => setCalendarMode(CalendarMode.DAY)} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${calendarMode === CalendarMode.DAY ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}>Day</button>
                <button onClick={() => setCalendarMode(CalendarMode.WEEK)} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${calendarMode === CalendarMode.WEEK ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}>Week</button>
                <button onClick={() => setCalendarMode(CalendarMode.MONTH)} className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${calendarMode === CalendarMode.MONTH ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}>Month</button>
              </div>
              <button onClick={() => { setSelectedBooking({ date: selectedDate.toISOString().split('T')[0] }); setView(ViewState.BOOKING_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700 shadow-md shadow-indigo-200"><Plus className="w-4 h-4"/> Add</button>
           </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex-1 relative shadow-inner">
           <div className="absolute inset-0 overflow-y-auto">
              {/* Day View */}
              {calendarMode === CalendarMode.DAY && (
                 <div className="relative min-h-[1040px]"> 
                    {HOURS.map(hour => (
                       <div key={hour} className="flex border-b border-gray-50 h-20 group">
                          <div className="w-16 flex-shrink-0 text-xs text-gray-400 p-2 text-right border-r border-gray-50 bg-gray-50/30 sticky left-0 z-10">{hour}:00</div>
                          <div className="flex-1 relative group-hover:bg-gray-50/20"></div>
                       </div>
                    ))}
                    {/* Current Time Line */}
                    <div className="absolute left-16 right-0 border-t-2 border-red-400 z-10 pointer-events-none opacity-50" style={{ top: `${((new Date().getHours() - 8) * 80) + ((new Date().getMinutes() / 60) * 80)}px` }}></div>

                    {appointments
                       .filter(a => new Date(a.startTime).toDateString() === selectedDate.toDateString())
                       .map(app => {
                          const start = new Date(app.startTime);
                          const top = ((start.getHours() - 8) * 80) + ((start.getMinutes() / 60) * 80);
                          const height = Math.max((app.duration / 60) * 80, 40); // Min height for visibility
                          return (
                             <div key={app.id} onClick={() => { setSelectedBooking(app); setView(ViewState.BOOKING_FORM); }}
                                className={`absolute left-20 right-4 rounded-lg border-l-4 p-2 shadow-sm cursor-pointer hover:shadow-md transition-all z-20 ${AppointmentStatus[app.status]?.bgColor} border-${AppointmentStatus[app.status]?.color}-500 opacity-90 hover:opacity-100`}
                                style={{ top: `${top}px`, height: `${height}px` }}>
                                <div className="flex justify-between items-start">
                                   <span className="font-bold text-xs text-gray-900">{app.customerName}</span>
                                   <span className="text-[10px] bg-white/60 px-1 rounded text-gray-700 font-mono">{formatTime(app.startTime)}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                                  {app.type === 'HOUSECALL' && <Truck className="w-3 h-3"/>}
                                  {services.find(s => s.id === app.serviceId)?.name}
                                </div>
                             </div>
                          );
                       })
                    }
                 </div>
              )}

              {/* Week View */}
              {calendarMode === CalendarMode.WEEK && (
                 <div className="flex min-w-[1000px] h-full">
                    <div className="w-14 flex-shrink-0 bg-gray-50 border-r border-gray-200 sticky left-0 z-30">
                       <div className="h-10 border-b border-gray-200 bg-gray-50"></div>
                       {HOURS.map(h => <div key={h} className="h-20 border-b border-gray-100 text-xs text-gray-400 p-1 text-center">{h}:00</div>)}
                    </div>
                    {Array.from({ length: 7 }).map((_, i) => {
                       const dayDate = new Date(selectedDate);
                       dayDate.setDate(dayDate.getDate() - dayDate.getDay() + i);
                       const isToday = new Date().toDateString() === dayDate.toDateString();
                       return (
                          <div key={i} className="flex-1 min-w-[120px] border-r border-gray-100 relative bg-white">
                             <div className={`h-10 border-b border-gray-200 text-center text-xs font-bold py-2 sticky top-0 z-20 ${isToday ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-700'}`}>
                                {DAYS[i]} {dayDate.getDate()}
                             </div>
                             <div className="relative h-[1040px]">
                                {HOURS.map(h => <div key={h} className="h-20 border-b border-gray-50"></div>)}
                                {appointments
                                   .filter(a => new Date(a.startTime).toDateString() === dayDate.toDateString())
                                   .map(app => {
                                      const start = new Date(app.startTime);
                                      const top = ((start.getHours() - 8) * 80) + ((start.getMinutes() / 60) * 80);
                                      const height = Math.max((app.duration / 60) * 80, 20);
                                      return (
                                         <div key={app.id} onClick={() => { setSelectedBooking(app); setView(ViewState.BOOKING_FORM); }}
                                            className={`absolute left-1 right-1 rounded border-l-2 p-1 text-[10px] cursor-pointer overflow-hidden ${AppointmentStatus[app.status]?.bgColor} border-${AppointmentStatus[app.status]?.color}-500 hover:z-30 hover:shadow-md transition-all`}
                                            style={{ top: `${top}px`, height: `${height}px` }}>
                                            <span className="font-bold block truncate text-gray-800">{app.customerName}</span>
                                         </div>
                                      );
                                   })
                                }
                             </div>
                          </div>
                       );
                    })}
                 </div>
              )}
              
              {/* Month View */}
              {calendarMode === CalendarMode.MONTH && (
                <div className="h-full flex flex-col">
                  <div className="grid grid-cols-7 border-b border-gray-200">
                    {DAYS.map(day => <div key={day} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase bg-gray-50">{day}</div>)}
                  </div>
                  <div className="grid grid-cols-7 auto-rows-fr flex-1 bg-gray-100 gap-px border-b border-gray-200">
                    {Array.from({length: getFirstDayOfMonth(selectedDate)}).map((_, i) => <div key={`empty-${i}`} className="bg-white min-h-[100px]"></div>)}
                    {Array.from({length: getDaysInMonth(selectedDate)}).map((_, i) => {
                      const dayNum = i + 1;
                      const dateStr = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), dayNum).toDateString();
                      const dayApps = appointments.filter(a => new Date(a.startTime).toDateString() === dateStr);
                      return (
                        <div key={dayNum} className="bg-white p-1 min-h-[100px] hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => { 
                             const d = new Date(selectedDate); d.setDate(dayNum); setSelectedDate(d); setCalendarMode(CalendarMode.DAY); 
                          }}>
                          <div className="text-right text-xs font-medium text-gray-500 mb-1">{dayNum}</div>
                          <div className="space-y-1">
                            {dayApps.slice(0, 4).map(app => (
                              <div key={app.id} className={`text-[9px] px-1 py-0.5 rounded truncate ${AppointmentStatus[app.status]?.bgColor} ${AppointmentStatus[app.status]?.textColor}`}>
                                {formatTime(app.startTime)} {app.customerName}
                              </div>
                            ))}
                            {dayApps.length > 4 && <div className="text-[9px] text-gray-400 text-center">+{dayApps.length - 4} more</div>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
           </div>
        </div>
     </div>
    );
  };

  const renderList = () => {
     const filtered = appointments.filter(a => statusFilter === 'ALL' ? true : a.status === statusFilter);
     
     return (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <div className="flex items-center gap-3 ml-2">
             <h2 className="text-lg font-bold text-gray-800">All Appointments</h2>
             <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border-none bg-gray-100 rounded-lg px-2 py-1 cursor-pointer focus:ring-0">
               <option value="ALL">All Statuses</option>
               {Object.values(AppointmentStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
             </select>
           </div>
           <button onClick={() => { setSelectedBooking(null); setView(ViewState.BOOKING_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> Add</button>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
           <div className="overflow-x-auto">
             <table className="w-full text-left">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
                   <tr><th className="px-6 py-3">Date</th><th className="px-6 py-3">Customer</th><th className="px-6 py-3">Service</th><th className="px-6 py-3">Type</th><th className="px-6 py-3">Status</th><th className="px-6 py-3">Price</th><th className="px-6 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                   {filtered.sort((a,b) => new Date(b.startTime) - new Date(a.startTime)).map(app => (
                      <tr key={app.id} className="hover:bg-gray-50 group transition-colors">
                         <td className="px-6 py-4 text-sm text-gray-600">
                            <span className="font-bold text-gray-900 block">{formatDate(app.startTime)}</span>
                            <span className="text-xs">{formatTime(app.startTime)}</span>
                         </td>
                         <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{app.customerName}</div>
                            <div className="text-xs text-gray-500">{app.customerPhone}</div>
                         </td>
                         <td className="px-6 py-4 text-gray-600 text-sm">{services.find(s => s.id === app.serviceId)?.name}</td>
                         <td className="px-6 py-4">
                            {app.type === 'HOUSECALL' 
                              ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-50 text-orange-700 text-xs font-medium"><Truck className="w-3 h-3"/> House Call</span>
                              : <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs font-medium"><MapPin className="w-3 h-3"/> On-Site</span>
                            }
                         </td>
                         <td className="px-6 py-4"><span className={`text-xs px-2 py-1 rounded-full font-medium ${AppointmentStatus[app.status]?.bgColor} ${AppointmentStatus[app.status]?.textColor}`}>{AppointmentStatus[app.status]?.label}</span></td>
                         <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency((app.price || 0) + (app.travelFee || 0))}</td>
                         <td className="px-6 py-4 text-right flex gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setSelectedBooking(app); setView(ViewState.BOOKING_FORM); }} className="text-gray-400 hover:text-indigo-600 bg-white p-1 rounded border border-gray-200 shadow-sm"><Edit2 className="w-4 h-4"/></button>
                            <button onClick={() => handleDelete('appointments', app.id)} className="text-gray-400 hover:text-red-600 bg-white p-1 rounded border border-gray-200 shadow-sm"><Trash2 className="w-4 h-4"/></button>
                         </td>
                      </tr>
                   ))}
                </tbody>
             </table>
           </div>
           {filtered.length === 0 && <div className="p-10 text-center text-gray-400">No appointments found.</div>}
        </div>
     </div>
     );
  };

  const renderStaff = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Staff Members</h2>
           <button onClick={() => { setSelectedStaff(null); setView(ViewState.STAFF_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> Add Staff</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {staff.map(s => (
              <div key={s.id} className="bg-white p-4 rounded-xl border border-gray-100 flex items-center justify-between hover:shadow-md transition-all">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-lg">{s.name.charAt(0)}</div>
                    <div>
                       <h4 className="font-bold text-gray-900">{s.name}</h4>
                       <p className="text-xs text-gray-500 uppercase tracking-wide">{s.role}</p>
                       <p className="text-xs text-gray-400 mt-1">{s.email}</p>
                    </div>
                 </div>
                 <div className="flex flex-col gap-2">
                    <button onClick={() => { setSelectedStaff(s); setView(ViewState.STAFF_FORM); }} className="text-gray-400 hover:text-indigo-600 bg-gray-50 p-2 rounded-lg hover:bg-indigo-50"><Edit2 className="w-4 h-4"/></button>
                    <button onClick={() => handleDelete('appointment-staff', s.id)} className="text-gray-400 hover:text-red-600 bg-gray-50 p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4"/></button>
                 </div>
              </div>
           ))}
        </div>
     </div>
  );

  const renderServices = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Services</h2>
           <button onClick={() => { setSelectedService(null); setView(ViewState.SERVICE_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> New Service</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {services.map(s => (
              <div key={s.id} className="bg-white p-5 rounded-xl border border-gray-100 hover:shadow-md transition-all flex flex-col justify-between h-full">
                 <div>
                    <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-gray-900 text-lg">{s.name}</h4>
                        <div className="flex gap-2">
                          <button onClick={() => { setSelectedService(s); setView(ViewState.SERVICE_FORM); }} className="text-gray-400 hover:text-indigo-600"><Edit2 className="w-4 h-4"/></button>
                          <button onClick={() => handleDelete('appointment-services', s.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">{s.description}</p>
                 </div>
                 <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 p-2 rounded-lg">
                       <Clock className="w-3 h-3"/> {s.duration} mins
                       {s.isHouseCallAvailable && <><span className="text-gray-300">|</span> <Truck className="w-3 h-3"/> House Call</>}
                    </div>
                    <div className="font-bold text-indigo-600 text-lg">{formatCurrency(s.basePrice)}</div>
                 </div>
              </div>
           ))}
        </div>
     </div>
  );

  const renderLocations = () => (
    <div className="space-y-4">
       <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 ml-2">Locations / Rooms</h2>
          <button onClick={() => { setSelectedLocation(null); setView(ViewState.LOCATION_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> Add Location</button>
       </div>
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {locations.map(l => (
             <div key={l.id} className="bg-white p-4 rounded-xl border border-gray-100 flex items-center justify-between hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                   <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold"><MapPin className="w-5 h-5"/></div>
                   <h4 className="font-bold text-gray-900">{l.name}</h4>
                </div>
                <div className="flex gap-1">
                   <button onClick={() => { setSelectedLocation(l); setView(ViewState.LOCATION_FORM); }} className="text-gray-400 hover:text-indigo-600 p-2"><Edit2 className="w-4 h-4"/></button>
                   <button onClick={() => handleDelete('appointment-locations', l.id)} className="text-gray-400 hover:text-red-600 p-2"><Trash2 className="w-4 h-4"/></button>
                </div>
             </div>
          ))}
       </div>
    </div>
 );

  if (loading) return <div className="flex justify-center h-full items-center"><Loader2 className="w-10 h-10 animate-spin text-indigo-600" /></div>;

  return (
    <div className="h-full flex flex-col bg-gray-50/50">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0 shadow-sm z-40">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
          <h1 className="text-xl font-bold text-gray-900 hidden md:block">Appointments</h1>
          <div className="h-6 w-px bg-gray-200 mx-2 hidden md:block" />
          <nav className="flex gap-1 overflow-x-auto no-scrollbar">
             {[
               { id: ViewState.DASHBOARD, label: 'Dashboard', icon: LayoutGrid },
               { id: ViewState.CALENDAR, label: 'Calendar', icon: Calendar },
               { id: ViewState.APPOINTMENTS_LIST, label: 'List', icon: List },
               { id: ViewState.SERVICES, label: 'Services', icon: Scissors },
               { id: ViewState.STAFF, label: 'Staff', icon: Users },
               { id: ViewState.LOCATIONS, label: 'Locations', icon: MapPin },
             ].map(item => (
               <button 
                key={item.id}
                onClick={() => setView(item.id)} 
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${view === item.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>
                 <item.icon className="w-4 h-4"/> {item.label}
               </button>
             ))}
          </nav>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto h-full">
          {view === ViewState.DASHBOARD && renderDashboard()}
          {view === ViewState.CALENDAR && renderCalendar()}
          {view === ViewState.APPOINTMENTS_LIST && renderList()}
          {view === ViewState.SERVICES && renderServices()}
          {view === ViewState.STAFF && renderStaff()}
          {view === ViewState.LOCATIONS && renderLocations()}
          
          {view === ViewState.BOOKING_FORM && (
            <BookingForm 
              booking={selectedBooking} 
              services={services} 
              staff={staff} 
              locations={locations} 
              onSave={(d) => handleSave('appointments', d, ViewState.CALENDAR)} 
              onCancel={() => setView(ViewState.CALENDAR)} 
              saving={saving} 
            />
          )}
          
          {view === ViewState.SERVICE_FORM && (
            <ServiceForm 
              service={selectedService} 
              onSave={(d) => handleSave('appointment-services', d, ViewState.SERVICES)} 
              onCancel={() => setView(ViewState.SERVICES)} 
              saving={saving} 
            />
          )}
          
          {view === ViewState.STAFF_FORM && (
            <StaffForm 
              staff={selectedStaff} 
              onSave={(d) => handleSave('appointment-staff', d, ViewState.STAFF)} 
              onCancel={() => setView(ViewState.STAFF)} 
              saving={saving} 
            />
          )}

          {view === ViewState.LOCATION_FORM && (
            <LocationForm 
              location={selectedLocation} 
              onSave={(d) => handleSave('appointment-locations', d, ViewState.LOCATIONS)} 
              onCancel={() => setView(ViewState.LOCATIONS)} 
              saving={saving} 
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentTool;